import CampaignLayer from "../components/CampaignLayer";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, ArrowUpRight, Briefcase, UserRound, BadgeCheck } from "lucide-react";
import { api, fileUrl, SITE_URL } from "../lib/api";
import Seo from "../components/Seo";
import JobCard from "../components/JobCard";
import Reveal from "../components/Reveal";
import LocationInput from "../components/LocationInput";

const POPULAR = ["Warehouse Operative", "HGV Driver", "Administrator", "Care Assistant", "Forklift Driver", "Cleaner"];

export default function Home() {
  const [query, setQuery] = useState("");
  const [location, setLocation] = useState("");
  const [latest, setLatest] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [spotlight, setSpotlight] = useState(null);
  const [offer, setOffer] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [insights, setInsights] = useState([]);
  const [insightTick, setInsightTick] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/insights").then((r) => {
      const items = r.data.items || [];
      const featuredFirst = [...items.filter((i) => i.featured), ...items.filter((i) => !i.featured)];
      setInsights(featuredFirst.slice(0, 6));
    }).catch(() => {});
  }, []);

  // gentle rotation: one card swaps every 9s, fixed card heights mean nothing moves
  useEffect(() => {
    if (insights.length < 3) return undefined;
    const t = setInterval(() => setInsightTick((x) => x + 1), 9000);
    return () => clearInterval(t);
  }, [insights.length]);

  useEffect(() => {
    Promise.all([
      api.get("/jobs", { params: { limit: 6 } }),
      api.get("/jobs", { params: { limit: 3, featured: true } }),
      api.get("/spotlight"),
      api.get("/settings/public"),
      api.get("/pricing"),
    ]).then(([latestRes, featuredRes, spotRes, settingsRes, pricingRes]) => {
      const feat = featuredRes.data.items || [];
      const lat = latestRes.data.items || [];
      // balanced mixture: 2 fresh roles, 1 Featured, 2 fresh, 1 Featured — never all-Featured,
      // never the same static set; empty Featured slots are filled by the next fresh role
      const seen = new Set();
      const fresh = lat.filter((j) => !j.featured && !seen.has(j.id) && seen.add(j.id));
      const feats = feat.filter((j) => !seen.has(j.id) && seen.add(j.id));
      const mixed = [];
      let fp = 0, sp2 = 0;
      const takeFresh = () => (sp2 < fresh.length ? fresh[sp2++] : null);
      const takeFeat = () => (fp < feats.length ? feats[fp++] : null);
      for (let slot = 0; slot < 6; slot++) {
        const pick = slot % 3 === 2 ? (takeFeat() || takeFresh()) : (takeFresh() || takeFeat());
        if (pick) mixed.push(pick);
      }
      setFeatured(mixed);
      const mixedIds = new Set(mixed.map((j) => j.id));
      setLatest(lat.filter((j) => !mixedIds.has(j.id)));
      setSpotlight(spotRes.data);
      setOffer(settingsRes.data.launch_offer);
      setPricing(pricingRes.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const search = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query.trim()) params.set("query", query.trim());
    if (location.trim()) params.set("location", location.trim());
    navigate(`/jobs${params.toString() ? `?${params}` : ""}`);
  };

  return (
    <main data-testid="home-page">
      <Seo
        title="Find Jobs & Post a Job Free | HiredToday"
        description="Search live UK jobs or advertise a vacancy free. HiredToday offers simple, affordable job advertising for employers."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            { "@type": "Organization", name: "HiredToday", url: SITE_URL, logo: `${SITE_URL}/logo.png`,
              description: "Search live UK jobs or advertise a vacancy free. Simple, affordable job advertising for UK employers.",
              areaServed: "GB", email: "office@hiredtoday.co.uk",
              contactPoint: { "@type": "ContactPoint", email: "office@hiredtoday.co.uk",
                contactType: "customer support", areaServed: "GB", availableLanguage: "English" } },
            { "@type": "WebSite", name: "HiredToday", url: SITE_URL,
              potentialAction: { "@type": "SearchAction", target: `${SITE_URL}/jobs?q={search_term_string}`, "query-input": "required name=search_term_string" } },
          ],
        }}
      />

      {/* Hero */}
      {offer?.enabled && offer.text && (
        <div className="bg-zinc-950 text-white text-center text-xs sm:text-sm font-medium px-4 py-2.5" data-testid="launch-offer-banner">
          {offer.text}
        </div>
      )}
      <section className="relative overflow-hidden flex items-center min-h-[calc(100svh-4rem)]">
        <div aria-hidden="true" className="absolute inset-0">
          <div className="liquid-blob blob-a w-[520px] h-[520px] -top-40 -left-32" style={{ background: "#BFDBFE" }} />
          <div className="liquid-blob blob-b w-[460px] h-[460px] top-10 -right-32" style={{ background: "#FDE68A" }} />
          <div className="liquid-blob blob-c w-[380px] h-[380px] top-80 left-1/3" style={{ background: "#E0E7FF" }} />
          <div className="liquid-blob blob-a w-[440px] h-[440px] -bottom-44 -left-24" style={{ background: "#BFDBFE" }} />
          <div className="liquid-blob blob-b w-[400px] h-[400px] -bottom-32 right-1/4" style={{ background: "#FDE68A" }} />
        </div>
        <CampaignLayer />

        <div className="relative w-full max-w-4xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-24 sm:pb-32 text-center">
          <h1 className="animate-fade-up font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black text-zinc-950 leading-[1.02]">
            The Search, Settled.
          </h1>
          <p className="animate-fade-up mt-6 sm:mt-7 text-sm sm:text-base text-zinc-600 max-w-2xl mx-auto" style={{ animationDelay: "100ms" }}>
            The right job can change a life. The right person can change a business. HiredToday gives both a better chance of being found.
          </p>

          <form onSubmit={search} data-testid="hero-search-form"
            className="animate-fade-up glass-deep rounded-lg mt-10 sm:mt-12 p-2.5 flex flex-col sm:flex-row gap-2.5 max-w-2xl mx-auto text-left"
            style={{ animationDelay: "200ms" }}>
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
              <input
                data-testid="hero-search-keywords"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Job title, keyword or company"
                className="w-full h-12 pl-11 pr-4 bg-white/80 border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </div>
            <LocationInput
              value={location}
              onChange={setLocation}
              testid="hero-search-location"
              placeholder="Town or postcode"
              className="sm:w-52"
            />
            <button type="submit" data-testid="hero-search-submit"
              className="group h-12 px-7 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 inline-flex items-center justify-center gap-2">
              Search jobs
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </form>

        </div>
      </section>

      {/* Featured employer spotlight */}
      {spotlight?.company && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-14" data-testid="spotlight-section">
          <Reveal>
            <div className="glass rounded-lg p-6 sm:p-8">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-5">Featured employer</p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                {spotlight.company.logo ? (
                  <img src={fileUrl(spotlight.company.logo)} alt={`${spotlight.company.name} logo`} data-testid="spotlight-logo"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="shrink-0 h-14 w-14 rounded-md object-cover border border-zinc-200" />
                ) : (
                  <div className="shrink-0 h-14 w-14 rounded-md bg-zinc-950 text-white flex items-center justify-center font-display font-black text-xl">
                    {spotlight.company.name?.charAt(0)?.toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/companies/${spotlight.company.slug}`} data-testid="spotlight-company"
                      className="font-display text-xl font-bold text-zinc-950 hover:text-blue-700 transition-colors duration-200">
                      {spotlight.company.name}
                    </Link>
                    {spotlight.company.verified && (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full" data-testid="spotlight-verified">
                        <BadgeCheck className="h-3.5 w-3.5" /> Verified
                      </span>
                    )}
                  </div>
                  {spotlight.company.description && (
                    <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed line-clamp-2">{spotlight.company.description}</p>
                  )}
                </div>
                <Link to={`/companies/${spotlight.company.slug}`} data-testid="spotlight-view"
                  className="shrink-0 inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-950 hover:text-blue-700 transition-colors duration-200">
                  View company <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              {spotlight.jobs.length > 0 && (
                <div className="mt-5 pt-5 border-t border-zinc-100 flex flex-wrap gap-2" data-testid="spotlight-jobs">
                  {spotlight.jobs.map((j) => (
                    <Link key={j.id} to={`/jobs/${j.slug}`} data-testid={`spotlight-job-${j.slug}`}
                      className="text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-full px-3.5 py-1.5 hover:border-zinc-400 transition-colors duration-200">
                      {j.title} · {j.location}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </Reveal>
        </section>
      )}

      {/* Featured jobs */}
      {featured.length > 0 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-14" data-testid="featured-jobs-section">
          <Reveal>
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-bold text-zinc-950">Featured &amp; new</h2>
              <span className="text-[11px] font-bold uppercase tracking-wider bg-yellow-400 text-zinc-950 px-2.5 py-1 rounded">Fresh mix</span>
            </div>
          </Reveal>
          <div className="grid gap-4">
            {featured.map((job, i) => (
              <Reveal key={job.id} delay={i * 80}>
                <JobCard job={job} />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Latest jobs */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-14" data-testid="latest-jobs-section">
        <Reveal>
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-bold text-zinc-950">Latest jobs</h2>
            <Link to="/jobs" data-testid="browse-all-jobs"
              className="group inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-950 hover:text-blue-700 transition-colors duration-200">
              All jobs
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>
        {loaded && latest.length === 0 ? (
          <Reveal>
            <div className="glass rounded-lg p-12 text-center" data-testid="jobs-empty-state">
              <p className="font-display text-xl font-bold text-zinc-900">New roles land here daily.</p>
              <p className="mt-2 text-sm text-zinc-500">Check back soon, or post the first one.</p>
              <Link to="/post-a-job" data-testid="empty-state-post-job"
                className="mt-6 inline-flex items-center gap-2 bg-zinc-950 text-white text-sm font-semibold px-6 py-3 rounded-md hover:bg-zinc-800 transition-colors duration-200">
                Post a job <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        ) : (
          <div className="grid gap-4">
            {latest.map((job, i) => (
              <Reveal key={job.id} delay={i * 60}>
                <JobCard job={job} />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* Featured insights */}
      {insights.length >= 2 && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="featured-insights">
          <Reveal>
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-bold text-zinc-950">Featured insights</h2>
              <Link to="/resources" data-testid="insights-all-link"
                className="group inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-950 hover:text-blue-700 transition-colors duration-200">
                Career &amp; market insights
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {[0, 1].map((slot) => {
              const item = insights[(insightTick + slot) % insights.length];
              const meta = `${item.origin === "original" ? "HiredToday" : item.source_name}${item.published_at ? ` · ${new Date(item.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}`;
              const body = (
                <>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{item.category}</span>
                  <h3 className="mt-4 font-display text-lg font-bold text-zinc-950 leading-snug group-hover:text-blue-800 transition-colors duration-200">{item.title}</h3>
                  <p className="mt-auto pt-4 text-xs text-zinc-400">{meta}</p>
                </>
              );
              const cls = "group glass rounded-lg p-7 flex flex-col min-h-[11rem] hover:shadow-lg transition-shadow duration-300 animate-fade-up";
              return item.origin === "original" ? (
                <Link key={`${slot}-${item.id}`} to={`/insights/${item.slug}`} data-testid={`insight-card-${slot}`} className={cls}>{body}</Link>
              ) : (
                <a key={`${slot}-${item.id}`} href={item.link} target="_blank" rel="noopener noreferrer" data-testid={`insight-card-${slot}`} className={cls}>{body}</a>
              );
            })}
          </div>
        </section>
      )}

      {/* Pricing strip */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-4" data-testid="pricing-strip">
        <Reveal>
          <div className="glass rounded-lg px-6 sm:px-10 py-8 flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              {[
                { name: "Free", id: "free" },
                { name: "Standard", id: "standard" },
                { name: "Featured", id: "featured" },
              ].map((t) => (
                <div key={t.name} data-testid={`pricing-strip-${t.name.toLowerCase()}`} className="flex items-baseline gap-2">
                  <span className="font-display text-2xl font-black text-zinc-950">
                    {t.id === "free" ? "Free" : `£${pricing?.[t.id]?.price ?? (t.id === "standard" ? 20 : 79)}`}
                  </span>
                  <span className="text-sm text-zinc-500">{t.name}</span>
                </div>
              ))}
            </div>
            <Link to="/pricing" data-testid="pricing-strip-link"
              className="group shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-zinc-950 hover:text-blue-700 transition-colors duration-200">
              See what each includes
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
